<template>
  <div class="merch-config-table">
    <vs-table
      search
      stripe
      border
      :sst="true"
      :data="table.data"
      :max-items="table.length"
      :total="table.total"
      @search="handleSearch"
      @change-page="handleChangePage"
      @sort="handleSort"
    >
      <template slot="header">
        <vs-dropdown vs-trigger-click class="cursor-pointer">
          <div
            class="p-3 border border-solid d-theme-border-grey-light rounded-full d-theme-dark-bg cursor-pointer flex items-center justify-between font-medium"
          >
            <span class="mr-2">
              {{ table.start }} - {{ table.end }} of {{ table.total }}
            </span>
            <feather-icon icon="ChevronDownIcon" svgClasses="h-4 w-4" />
          </div>
          <vs-dropdown-menu>
            <vs-dropdown-item
              v-for="item in table.limits"
              :key="item"
              @click="handleChangelength(item)"
            >
              <span>{{ item }}</span>
            </vs-dropdown-item>
          </vs-dropdown-menu>
        </vs-dropdown>
      </template>

      <template slot="thead">
        <vs-th class="th-action"></vs-th>
        <vs-th sort-key="territory">Territory</vs-th>
        <vs-th sort-key="merchandising_id">Merchandising ID</vs-th>
        <vs-th sort-key="display_valid_from">Valid From</vs-th>
        <vs-th sort-key="display_valid_to">Valid To</vs-th>
      </template>

      <template slot-scope="{ data }">
        <vs-tr
          :data="tr"
          :key="indextr"
          v-for="(tr, indextr) in data"
        >
          <vs-td>
            <div class="action-btns">
              <vs-button
                v-if="hasAllowedAction(data[indextr], 'edit')"
                @click.stop="handleEdit(data[indextr].id)"
                size="small"
                color="primary"
                icon-pack="feather"
                icon="icon-edit-2"
                title="Edit"
              />
              <vs-button
                v-if="hasAllowedAction(data[indextr], 'view')"
                @click.stop="handleView(data[indextr].id)"
                size="small"
                color="primary"
                icon-pack="feather"
                icon="icon-eye"
                title="View"
              />
              <vs-button
                v-if="hasAllowedAction(data[indextr], 'release')"
                @click.stop="handleRelease(data[indextr].id)"
                size="small"
                color="primary"
                icon-pack="feather"
                icon="icon-check"
                title="Release"
              />
              <vs-button
                v-if="hasAllowedAction(data[indextr], 'cancel')"
                @click.stop="openCancelDialog(data[indextr].id)"
                size="small"
                color="primary"
                icon-pack="feather"
                icon="icon-x"
                title="Cancel"
              />
            </div>
          </vs-td>
          <vs-td>
            {{ formatRowTerritory(data[indextr]) }}
          </vs-td>
          <vs-td>
            {{ data[indextr].merchandising_id }}
          </vs-td>
          <vs-td>
            {{
              data[indextr].display_valid_from || data[indextr].valid_from || "-"
            }}
          </vs-td>
          <vs-td>
            {{
              data[indextr].display_valid_to || data[indextr].valid_to || "-"
            }}
          </vs-td>
        </vs-tr>
      </template>
    </vs-table>

    <p v-if="!table.data.length && !loading" class="empty-hint mt-4">
      No merchandising configuration found for this tab.
    </p>

    <vs-popup
      class="merch-cancel-popup"
      title="Cancel Configuration"
      :active.sync="cancelPopupActive"
    >
      <div class="mb-4">
        <label class="vs-input--label">Reason</label>
        <vs-textarea v-model="cancelReason" placeholder="Enter cancel reason" />
      </div>
      <div class="flex justify-end">
        <vs-button
          class="mr-2"
          type="border"
          @click="cancelPopupActive = false"
        >
          Back
        </vs-button>
        <vs-button color="danger" @click="confirmCancel">Confirm Cancel</vs-button>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import {
  BASE_URL,
  isApiSuccess,
  hasAllowedAction,
  formatTerritoryLabel,
} from "./api";

export default {
  name: "MerchandisingConfigurationDataTable",
  props: {
    statusTab: {
      type: String,
      required: true,
    },
    territoryId: {
      type: [Number, String],
      default: null,
    },
    merchandisingIdFilter: {
      type: String,
      default: "",
    },
    refreshKey: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      loading: false,
      cancelPopupActive: false,
      cancelReason: "",
      cancelTargetId: null,
      table: {
        data: [],
        length: 10,
        page: 1,
        search: "",
        order: "id",
        sort: "desc",
        total: 0,
        totalPage: 1,
        totalSearch: 0,
        limits: [10, 25, 50, 100],
        start: 0,
        end: 0,
      },
    };
  },
  watch: {
    statusTab() {
      this.table.page = 1;
      this.getData();
    },
    refreshKey() {
      this.table.page = 1;
      this.getData();
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    hasAllowedAction,
    formatRowTerritory(row) {
      if (row.territory) {
        return formatTerritoryLabel(row.territory);
      }
      const code = row.territory_code || "";
      const name = row.territory_name || "";
      return `${code} ${name}`.trim() || "-";
    },
    handleEdit(id) {
      this.$router.push({
        name: "merchandising-configuration-edit",
        params: { id: String(id) },
      });
    },
    handleView(id) {
      this.$router.push({
        name: "merchandising-configuration-view",
        params: { id: String(id) },
      });
    },
    handleSearch(searching) {
      this.table.search = searching;
      this.table.page = 1;
      this.getData();
    },
    handleChangePage(page) {
      this.table.page = page;
      this.getData();
    },
    handleSort(key, direction) {
      this.table.order = key;
      this.table.sort = direction;
      this.getData();
    },
    handleChangelength(length) {
      this.table.length = length;
      this.table.page = 1;
      this.getData();
    },
    getListParams() {
      const params = {
        status: this.statusTab,
        page: this.table.page,
        length: this.table.length,
        order: this.table.order,
        sort: this.table.sort,
      };
      if (this.table.search) {
        params.search = this.table.search;
      }
      if (this.territoryId) {
        params.territory_id = this.territoryId;
      }
      if (this.merchandisingIdFilter) {
        params.merchandising_id = this.merchandisingIdFilter;
      }
      return params;
    },
    getData() {
      this.loading = true;
      this.$vs.loading();
      this.$http
        .get(BASE_URL, { params: this.getListParams() })
        .then((resp) => {
          if (isApiSuccess(resp) && resp.data) {
            this.table.data = resp.data.records || [];
            this.table.total = resp.data.total_record || 0;
            this.table.totalPage = resp.data.total_page || 1;
            this.table.totalSearch = resp.data.total_record_search || 0;
            if (resp.data.total_record_per_page) {
              this.table.length = resp.data.total_record_per_page;
            }
            this.setStartEnd();
          } else {
            this.table.data = [];
            this.notifyError(resp.message || "Failed to load data");
          }
          this.loading = false;
          this.$vs.loading.close();
        })
        .catch(() => {
          this.loading = false;
          this.$vs.loading.close();
          this.notifyError("Failed to load data");
        });
    },
    setStartEnd() {
      let valStart = this.table.length * this.table.page - this.table.length + 1;
      if (valStart > this.table.total) valStart = 1;
      if (this.table.total === 0) valStart = 0;
      let valEnd = this.table.length * this.table.page;
      if (valEnd > this.table.total) valEnd = this.table.total;
      if (
        this.table.totalSearch < this.table.total &&
        this.table.search !== ""
      ) {
        valEnd = this.table.totalSearch;
      }
      this.table.start = valStart;
      this.table.end = valEnd;
    },
    handleRelease(id) {
      this.$vs.dialog({
        type: "confirm",
        color: "primary",
        title: "Confirm Release",
        text: "Are you sure to release this configuration?",
        accept: () => {
          this.$vs.loading();
          this.$http
            .post(`${BASE_URL}/${id}/release`)
            .then((resp) => {
              this.$vs.loading.close();
              if (isApiSuccess(resp)) {
                this.notifySuccess(
                  resp.message || "Configuration released successfully"
                );
                this.getData();
              } else {
                this.notifyError(resp.message || "Failed to release");
              }
            })
            .catch(() => {
              this.$vs.loading.close();
              this.notifyError("Failed to release");
            });
        },
      });
    },
    openCancelDialog(id) {
      this.cancelTargetId = id;
      this.cancelReason = "";
      this.cancelPopupActive = true;
    },
    confirmCancel() {
      if (!this.cancelReason || !this.cancelReason.trim()) {
        this.notifyError("Cancel reason is required");
        return;
      }
      this.$vs.loading();
      this.$http
        .post(`${BASE_URL}/${this.cancelTargetId}/cancel`, {
          reason: this.cancelReason.trim(),
        })
        .then((resp) => {
          this.$vs.loading.close();
          if (isApiSuccess(resp)) {
            this.cancelPopupActive = false;
            this.notifySuccess(
              resp.message || "Configuration canceled successfully"
            );
            this.getData();
          } else {
            this.notifyError(resp.message || "Failed to cancel");
          }
        })
        .catch(() => {
          this.$vs.loading.close();
          this.notifyError("Failed to cancel");
        });
    },
    notifySuccess(text) {
      this.$vs.notify({
        color: "success",
        title: "Success",
        text,
        position: "top-right",
      });
    },
    notifyError(text) {
      this.$vs.notify({
        color: "danger",
        title: "Error",
        text,
        position: "top-right",
      });
    },
  },
};
</script>

<style scoped>
.th-action {
  width: 140px;
}

.action-btns {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.empty-hint {
  color: rgba(var(--vs-text), 0.6);
  font-size: 14px;
}
</style>
